import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  output,
  signal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import type { MarketingStoryDetail } from './shared/marketing-story-detail.model';
import { parseMilestoneCtaLink } from '../utils/milestone-cta';

@Component({
  selector: 'app-marketing-story-detail-modal',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="cg-mkt-story-modal-backdrop"
      (click)="onBackdropClick($event)"
      role="presentation"
    >
      <div
        class="cg-mkt-story-modal"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="titleId"
        (click)="$event.stopPropagation()"
      >
        <header class="cg-mkt-story-modal__header">
          <h2 class="cg-mkt-story-modal__title" [id]="titleId">{{ detail.name }}</h2>
          <button
            type="button"
            class="cg-mkt-story-modal__close"
            (click)="closed.emit()"
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div class="cg-mkt-story-modal__body">
          @if (hasImage()) {
            <div class="cg-mkt-story-modal__media">
              <img
                [src]="detail.image!.cdnUrl"
                [alt]="detail.image?.alt ?? detail.name"
                loading="lazy"
                (error)="onImageError()"
              />
            </div>
          } @else {
            <div class="cg-mkt-story-modal__media cg-mkt-story-modal__media--placeholder" aria-hidden="true">
              <span>{{ initials }}</span>
            </div>
          }

          <div class="cg-mkt-story-modal__content">
            @if (detail.category) {
              <span class="cg-mkt-story-modal__category">{{ detail.category }}</span>
            }

            @if (detail.rating != null) {
              <div
                class="cg-mkt-story-modal__stars"
                [attr.aria-label]="detail.rating + ' out of 5 stars'"
              >
                @for (star of starRange; track star) {
                  <span aria-hidden="true" [class.is-dim]="star > detail.rating!">★</span>
                }
                <span class="cg-mkt-story-modal__rating-value">{{ detail.rating!.toFixed(1) }}</span>
              </div>
            }

            <blockquote class="cg-mkt-story-modal__quote">"{{ detail.description }}"</blockquote>

            <dl class="cg-mkt-story-modal__facts">
              @if (detail.university) {
                <div class="cg-mkt-story-modal__fact">
                  <dt>University</dt>
                  <dd>{{ detail.university }}</dd>
                </div>
              }
              @if (detail.country) {
                <div class="cg-mkt-story-modal__fact">
                  <dt>Country</dt>
                  <dd>{{ detail.country }}</dd>
                </div>
              }
              @if (detail.program) {
                <div class="cg-mkt-story-modal__fact">
                  <dt>Program</dt>
                  <dd>{{ detail.program }}</dd>
                </div>
              }
              @if (detail.intake) {
                <div class="cg-mkt-story-modal__fact">
                  <dt>Intake</dt>
                  <dd>{{ detail.intake }}</dd>
                </div>
              }
              @if (detail.meta && !detail.university && !detail.country && !detail.program) {
                <div class="cg-mkt-story-modal__fact">
                  <dd>{{ detail.meta }}</dd>
                </div>
              }
            </dl>

            @if (detail.ctaHref && detail.ctaLabel) {
              @let link = parseMilestoneCtaLink(detail.ctaHref);
              @if (link.kind === 'router') {
                <a
                  class="cg-mkt-story-modal__cta"
                  [routerLink]="link.path"
                  [fragment]="link.fragment"
                  (click)="closed.emit()"
                >
                  {{ detail.ctaLabel }}
                  <span aria-hidden="true">→</span>
                </a>
              } @else {
                <a class="cg-mkt-story-modal__cta" [href]="link.href">
                  {{ detail.ctaLabel }}
                  <span aria-hidden="true">→</span>
                </a>
              }
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class MarketingStoryDetailModalComponent implements OnChanges {
  readonly parseMilestoneCtaLink = parseMilestoneCtaLink;
  readonly starRange = [1, 2, 3, 4, 5] as const;
  readonly titleId = 'cg-mkt-story-modal-title';

  @Input({ required: true }) detail!: MarketingStoryDetail;
  readonly closed = output<void>();

  private readonly imageLoadFailed = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['detail']) {
      this.imageLoadFailed.set(false);
    }
  }

  get initials(): string {
    const parts = this.detail.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    const single = parts[0]?.charAt(0);
    return single ? single.toUpperCase() : '?';
  }

  hasImage(): boolean {
    if (this.imageLoadFailed()) return false;
    return Boolean(this.detail.image?.cdnUrl?.trim());
  }

  onImageError(): void {
    this.imageLoadFailed.set(true);
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closed.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }
}
